import React, { useState, useEffect, useCallback } from 'react';
import { getConfig, updateConfig } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Save, Loader2, AlertTriangle } from 'lucide-react';

const AppConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editedValues, setEditedValues] = useState({});
  const [savingKeys, setSavingKeys] = useState({});

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getConfig();
      setConfigs(response.data);
      // Initialize edited values
      const values = {};
      response.data.forEach(c => { values[c.key] = c.value; });
      setEditedValues(values);
    } catch (error) {
      toast.error('Failed to load config');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async (key) => {
    setSavingKeys(prev => ({ ...prev, [key]: true }));
    try {
      await updateConfig(key, editedValues[key]);
      toast.success('Saved successfully');
      fetchConfig();
    } catch (error) {
      toast.error('Error saving — try again');
    } finally {
      setSavingKeys(prev => ({ ...prev, [key]: false }));
    }
  };

  const hasChanged = (key) => {
    const original = configs.find(c => c.key === key);
    return original && original.value !== editedValues[key];
  };

  const renderConfigField = (config) => {
    const { key, description, value } = config;
    const currentValue = editedValues[key] ?? value;
    const changed = hasChanged(key);
    const saving = savingKeys[key];

    // Special handling for maintenanceMode
    if (key === 'maintenanceMode') {
      const isEnabled = currentValue === 'true';
      return (
        <Card key={key} className={isEnabled ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-semibold">{key}</Label>
                <p className="text-sm text-gray-500">{description}</p>
                {isEnabled && (
                  <div className="flex items-center gap-2 mt-2 text-red-600">
                    <AlertTriangle size={16} />
                    <span className="text-sm font-medium">App will show maintenance banner</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(v) => setEditedValues(prev => ({ ...prev, [key]: v ? 'true' : 'false' }))}
                />
                {changed && (
                  <Button size="sm" onClick={() => handleSave(key)} disabled={saving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                    {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Special handling for appAnnouncement
    if (key === 'appAnnouncement') {
      return (
        <Card key={key}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-semibold">{key}</Label>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
              {changed && (
                <Button size="sm" onClick={() => handleSave(key)} disabled={saving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-1" /> Save</>}
                </Button>
              )}
            </div>
            <Textarea
              value={currentValue}
              onChange={(e) => setEditedValues(prev => ({ ...prev, [key]: e.target.value }))}
              placeholder="Leave blank for no announcement"
              rows={3}
            />
            <p className="text-xs text-gray-400">Leave blank for no announcement.</p>
          </CardContent>
        </Card>
      );
    }

    // Default text input
    return (
      <Card key={key}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="font-semibold">{key}</Label>
              <p className="text-sm text-gray-500 mb-2">{description}</p>
              <Input
                value={currentValue}
                onChange={(e) => setEditedValues(prev => ({ ...prev, [key]: e.target.value }))}
              />
              {key === 'minAppVersion' && (
                <p className="text-xs text-gray-400 mt-1">Users below this version will see an update prompt.</p>
              )}
            </div>
            {changed && (
              <Button size="sm" onClick={() => handleSave(key)} disabled={saving} className="bg-[#1A6B3C] hover:bg-[#155a32]">
                {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-1" /> Save</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-4" data-testid="config-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">App Config</h1>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">No config keys found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Order: maintenanceMode first, then others */}
          {configs
            .sort((a, b) => {
              if (a.key === 'maintenanceMode') return -1;
              if (b.key === 'maintenanceMode') return 1;
              if (a.key === 'appAnnouncement') return -1;
              if (b.key === 'appAnnouncement') return 1;
              return a.key.localeCompare(b.key);
            })
            .map(renderConfigField)
          }
        </div>
      )}
    </div>
  );
};

export default AppConfigPage;
